import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getWhiteLabels, syncWhiteLabels } from '@/api/white-labels';
import type { WhiteLabel } from '@/types/white-label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tag,
  Plus,
  RefreshCw,
  Search,
  Star,
  ExternalLink,
  GraduationCap,
  Network,
  Server,
  Smartphone,
} from 'lucide-react';

type TabType = 'v2' | 'v1';

export function WhiteLabelsPage() {
  const navigate = useNavigate();
  const [whiteLabels, setWhiteLabels] = useState<WhiteLabel[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('v2');

  useEffect(() => {
    fetchWhiteLabels();
  }, []);

  const fetchWhiteLabels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getWhiteLabels();
      setWhiteLabels(data);
    } catch (err) {
      setError('Erreur lors du chargement des marques blanches');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      const result = await syncWhiteLabels();
      setSuccess(`Synchronisation terminee: ${result.created} creees, ${result.updated} mises a jour`);
      await fetchWhiteLabels();
    } catch (err) {
      setError('Erreur lors de la synchronisation');
      console.error(err);
    } finally {
      setSyncing(false);
    }
  };

  // Separate V1 and V2 white labels
  const v1WhiteLabels = whiteLabels.filter((wl) => !wl.v2TenantGroupId);
  const v2WhiteLabels = whiteLabels.filter((wl) => wl.v2TenantGroupId);

  const currentWhiteLabels = activeTab === 'v2' ? v2WhiteLabels : v1WhiteLabels;

  const filteredWhiteLabels = currentWhiteLabels.filter((wl) => {
    if (!searchValue) return true;
    const search = searchValue.toLowerCase();
    return (
      wl.name.toLowerCase().includes(search) ||
      wl.v2Host?.toLowerCase().includes(search) ||
      wl.school?.name.toLowerCase().includes(search) ||
      wl.schoolNetwork?.name.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="default" className="bg-green-100 text-green-700">Actif</Badge>;
      case 'INACTIVE':
        return <Badge variant="secondary">Inactif</Badge>;
      case 'SUSPENDED':
        return <Badge variant="destructive">Suspendu</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const getAssociationBadge = (wl: WhiteLabel) => {
    if (wl.schoolNetwork) {
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Network className="h-3 w-3 mr-1" />
            Reseau
          </Badge>
          <Link
            to={`/school-networks/${wl.schoolNetwork.id}`}
            className="text-sm hover:underline text-primary"
          >
            {wl.schoolNetwork.name}
          </Link>
        </div>
      );
    }
    if (wl.school) {
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <GraduationCap className="h-3 w-3 mr-1" />
            École
          </Badge>
          <Link
            to={`/schools/${wl.school.code}`}
            className="text-sm hover:underline text-primary"
          >
            {wl.school.name}
          </Link>
        </div>
      );
    }
    return <span className="text-muted-foreground text-sm">Non associee</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Tag className="h-5 w-5" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              Marques blanches
            </h1>
          </div>
          <p className="text-sm md:text-base text-muted-foreground pl-[52px]">
            Gestion des marques blanches V1 et V2.
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'v2' && (
            <>
              <Button variant="outline" onClick={handleSync} disabled={syncing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Synchronisation...' : 'Synchroniser V2'}
              </Button>
              <Button asChild>
                <Link to="/school-networks/new?type=physical">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer un réseau physique
                </Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div>
        <nav className="flex gap-1">
          <button
            onClick={() => setActiveTab('v2')}
            className={`flex items-center gap-2 py-2.5 px-4 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'v2'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Server className="h-4 w-4" />
            Marques blanches V2
            <Badge variant={activeTab === 'v2' ? 'outline' : 'secondary'} className={activeTab === 'v2' ? 'bg-white/20 text-white border-white/30' : ''}>
              {v2WhiteLabels.length}
            </Badge>
          </button>
          <button
            onClick={() => setActiveTab('v1')}
            className={`flex items-center gap-2 py-2.5 px-4 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === 'v1'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
            }`}
          >
            <Smartphone className="h-4 w-4" />
            Marques blanches V1
            <Badge variant={activeTab === 'v1' ? 'outline' : 'secondary'} className={activeTab === 'v1' ? 'bg-white/20 text-white border-white/30' : ''}>
              {v1WhiteLabels.length}
            </Badge>
          </button>
        </nav>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            &times;
          </button>
        </div>
      )}
      {success && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-700">
          {success}
          <button
            onClick={() => setSuccess(null)}
            className="ml-2 text-green-500 hover:text-green-700"
          >
            &times;
          </button>
        </div>
      )}

      {/* Tab description */}
      {activeTab === 'v1' && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
          <p>
            Les marques blanches V1 sont créées dans le formulaire de création d'école.
            <strong> Une école ne peut pas avoir à la fois une marque blanche V1 et V2.</strong>
          </p>
        </div>
      )}

      {/* Search and count */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-9"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {filteredWhiteLabels.length} marque{filteredWhiteLabels.length > 1 ? 's' : ''} blanche{filteredWhiteLabels.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
          Chargement...
        </div>
      ) : filteredWhiteLabels.length === 0 ? (
        <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
          Aucune marque blanche trouvee.
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden bg-card">
          {activeTab === 'v2' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Environnement</TableHead>
                  <TableHead>Association</TableHead>
                  <TableHead>Date création</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWhiteLabels.map((wl) => (
                  <TableRow key={wl.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/white-labels/${wl.id}`}
                          className="font-medium hover:underline text-primary"
                        >
                          {wl.name}
                        </Link>
                        {wl.isDefault && (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                            <Star className="h-3 w-3 mr-1" />
                            Défaut
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {wl.v2Host ? (
                        <span className="font-mono text-sm">{wl.v2Host}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(wl.v2Status)}</TableCell>
                    <TableCell>
                      {wl.activeEnvironment ? (
                        <Badge
                          variant="outline"
                          className={
                            wl.activeEnvironment === 'v2'
                              ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          }
                        >
                          {wl.activeEnvironment.toUpperCase()}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getAssociationBadge(wl)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(wl.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/white-labels/${wl.id}`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>URL Android</TableHead>
                  <TableHead>URL iOS</TableHead>
                  <TableHead>École</TableHead>
                  <TableHead>Date creation</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWhiteLabels.map((wl) => (
                  <TableRow key={wl.id}>
                    <TableCell>
                      <span className="font-medium">{wl.name}</span>
                    </TableCell>
                    <TableCell>
                      {wl.url ? (
                        <a
                          href={wl.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline truncate max-w-[200px] block"
                        >
                          {wl.url}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {wl.urlAndroid ? (
                        <a
                          href={wl.urlAndroid}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          Play Store
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {wl.urlIOS ? (
                        <a
                          href={wl.urlIOS}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          App Store
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {wl.school ? (
                        <Link
                          to={`/schools/${wl.school.code}`}
                          className="text-sm text-primary hover:underline"
                        >
                          {wl.school.name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">Non associee</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(wl.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}
